import React, {
    useState,
    useRef,
    useEffect,
    Dispatch,
    SetStateAction, UIEventHandler,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation, useParams } from "react-router-dom";

import { useOutsideAlerter } from "utils/hooks/useOnClickOutside";
import { Button, IcoThreeDots, TextPlain, TitleH2 } from "components/generic";
import {
    ItemLi,
    DropDownMenu,
    ConversationList,
    Conversations,
    ConversationsTitle, DeleteChatPopover, ConversationSearchSection, InviteWrapper,
} from "./styles";
import { theme } from "styles/theme";
import { LoaderCustom } from "components/Loader";

import {
    API_CHATS, API_MATCHES,
    API_STUDENT,
    API_USERS,
    HOST_CHATS_ROUTE,
} from "utils/constants";
import { useContextHospi } from "context/ContextHospi";
import { useContextHost } from "context/ContextHost";
import { UserAPI } from "API/userApi";
import { ChatsAPI } from "API/chatsApi";
import {
    IChater,
    IDataConv,
    ISingleConversation, ISocketResponse,
    TConversation,
    THandleSingleConv,
    TModeConv,
    TUserShot,
} from "interfaces/interfacesChat";
import Modal from "components/Modal";
import {DialogWrapper} from "components/containers";
import Calendar, {useCalendar} from "components/Calendar";
import {IHousesShotInfo, IMatchesEnum, IPerson} from "interfaces/intarfaces";
import {convertTime, getPeriod, isoDate} from "utils/helpers";
import {handleMatch} from "utils/handleMatch";
import {StudentAPI} from "API/studentApi";
import {HostAPI} from "../../../API/hostApi";

type TParamsPath = { user_id: string; house_id: string };

interface IChaterInitial extends React.HTMLAttributes<HTMLDivElement> {
    lastMessageDate: any
    setActiveConv: Dispatch<SetStateAction<IDataConv>>;
    activeConv: IDataConv;
    showProfile: (userID: string) => void;
    setLatestRead: (time: string[]) => void;
    isEmpty: (empty: boolean) => void;
    isPhone: boolean;
    unreadConversations: string[]
    closeChat: (flag: boolean) => void
    setUnreadConversations: Dispatch<SetStateAction<string[]>>
    setLoading: (flag: boolean) => void
    messageData: ISocketResponse | null
}

export const Chater: React.FC<IChaterInitial> = ({
                                                     lastMessageDate,
                                                     setActiveConv,
                                                     activeConv,
                                                     isEmpty,
                                                     isPhone,
                                                     showProfile,
                                                     setLatestRead,
                                                     unreadConversations,
                                                     setUnreadConversations,
                                                     closeChat,
                                                     messageData,
                                                     setLoading: setLoader,
                                                     ...props
                                                 }) => {
    const {t, i18n} = useTranslation("chats");
    const {t: t_match} = useTranslation("matches");
    const DAY_UNIT_IN_MILLISECONDS = 24 * 3600 * 1000

    const {userProfile} = useContextHospi();
    const {housesShotData} = useContextHost();
    const [conversations, setConversations] = useState<TConversation[]>([]);
    const [searchedConversations, setSearchedConversations] = useState<TConversation[]>([])
    const [showSearched, setShowSearched] = useState<boolean>(false)
    const [searchProgress, setSearchProgress] = useState<boolean>(false)
    const [chater, setChater] = useState<IChater[]>([]);
    const [searchedChatter, setSearchedChatter] = useState<IChater[]>([])
    const [showModal, setShowModal] = useState<number>(0);
    const [loading, setLoading] = useState(false);
    const [curerntChat, setCurrentChat] = useState("");
    const [deleteChatPopover, setDeleteChatPopover] = useState<string>('')
    const [loadAdditionalConv, setLoadAdditionalConv] = useState<number>(0)
    const [batchToken, setBatchToken] = useState<string>('')
    const [disableInviteButton, setDisableInviteButton] = useState<string[]>([])
    const [houseName, setHouseName] = useState<string>('')
    const [scrolled, setScrolled] = useState<boolean>(false)
    const [searchPattern, setSearchPattern] = useState<string>('')
    const [inviteError, setInviteError] = useState<string>('')
    const [scrollAfterClear, setScrollAfterClear] = useState<boolean>(true)
    const [typedAfterSearch, setTypedAfterSearch] = useState<boolean>(false)
    const [lastSearched, setLastSearched] = useState<string>('')
    const [searchPage, setSearchPage] = useState<number>(1)
    const convRef = useRef<any>(null)

    const handleSearchInputChange = (e: string) => {
        setSearchPattern(e)
    }

    useEffect(() => {
        if((lastSearched && (searchPattern !== lastSearched))) {
            setTypedAfterSearch(true)
        } else {
            setTypedAfterSearch(false)
        }
        if(!searchPattern) {
            setTypedAfterSearch(false)
            setShowSearched(false)
            setSearchedConversations([])
        }
    }, [lastSearched, searchPattern])

    const handleSearchChats = () => {
        convRef.current && convRef.current.scrollTo(0,0)
        setSearchProgress(true)
        setLastSearched(searchPattern)
        setSearchPage(1)
        ChatsAPI.get(API_CHATS + "query-chats/" + userProfile?.user_id! + `?search_pattern=${searchPattern}`).then(
            (res) => {
                setSearchedChatter(res.chats);
            }
        ).finally(() => {
            setTypedAfterSearch(false)
            setSearchProgress(false)
            setShowSearched(true)
        })
    }

    const handleEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" && searchPattern.trim()) {
            handleSearchChats();
            e.preventDefault();
        }
    };

    const refs = conversations.reduce((acc, value) => {
        if(value && value.chat_id) {
            acc[value.chat_id] = React.createRef();
        }
        return acc;
    }, {} as any)


    const scrollToChat = (id: string) =>
        refs[id]?.current?.scrollIntoView({ behavior: "smooth", block: 'nearest', inline: 'start' });

    useEffect(() => {
        if(scrollAfterClear) {
            scrollToChat(activeConv.chat_id)
            setScrollAfterClear(false)
        }
    }, [scrollAfterClear, activeConv.chat_id])

    const location = useLocation();
    const navigate = useNavigate()
    const modePage =
        location.pathname === `/${i18n.language}${HOST_CHATS_ROUTE.slice(5)}` ? "withoutselected" : "preselected";

    let { user_id, house_id } = useParams();

    const typeUserID =
        userProfile?.user_type === "host" ? "student_id" : "host_id";

    const dateRange = useCalendar({
        ...getPeriod(),
        range: true,
    });

    const [sorted, setSorted] = useState<boolean>(false)

    const uniqueIds = new Set();

    const unique = (arr: TConversation[]) => arr.filter(element => {
        if(element && arr.length) {
            const isDuplicate = uniqueIds.has(element.chat_id);

            uniqueIds.add(element.chat_id);

            if (!isDuplicate) {
                return true;
            }

            return false;
        } else {
            return false;
        }
    });

    useEffect(() => {
        setSorted(false)
    }, [messageData])

    useEffect(() => {
        setSorted(false)
    }, [messageData])

    useEffect(() => {
        if(!sorted && messageData?.body.chat_id === activeConv.chat_id && messageData.action === 'text_message_created' && showSearched) {
            const newConv = searchedConversations.find(({chat_id}) => chat_id === activeConv.chat_id)
            setSearchedConversations((prev) => [newConv!, ...prev.filter((item) => item && (item.chat_id !== activeConv.chat_id))])
            // setConversations((prev) => [newConv!, ...prev.filter((item) => item && (item.chat_id !== activeConv.chat_id))])
            setSorted(true)
        }
    }, [messageData, showSearched, activeConv, sorted, conversations])

    useEffect(() => {
        setLoading(true);
        loadConv().finally(() => setLoading(false));
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        (conversations.length && userProfile?.user_type === 'host') && HostAPI.get(API_MATCHES + `host-matches/${userProfile?.user_id}`)
            .then((response) => {
                setDisableInviteButton(response.filter(({
                                                            house_id,
                                                            match_status
                                                        }: { house_id: string, match_status: number }) => conversations.filter((item) => item).map(({house_id}) => house_id).includes(house_id) && [3, 4, 6, 5].includes(match_status)).map(({house_id}: { house_id: string }) => house_id))
            })
    }, [conversations])

    const ref = useRef<any>(null)

    useEffect(() => {
        if(chater.length && house_id && user_id && !chater.some(({house_id: chater_house_id, student_id}) => chater_house_id === house_id && user_id === student_id)) {
            setLoadAdditionalConv(prev => prev + 1)
            ref.current = batchToken
        }
    }, [chater, house_id, user_id, batchToken, userProfile])

    useEffect(() => {
        const [_, q_chat, q_house, q_student, q_test] = location.search.split('=')
        if(ref.current !== batchToken && chater.length && !q_test && q_house && q_student && !activeConv.house_id && !chater.some(({house_id: chater_house_id, student_id}) => chater_house_id === q_house.slice(0,-2) && student_id === q_student)) {
            setLoadAdditionalConv(prev => prev + 1)
            ref.current = batchToken

        }
    }, [chater, location.search, batchToken, activeConv, ref.current])

    useEffect(() => {
        if(activeConv.chat_id && !scrolled) {
            scrollToChat(activeConv.chat_id)
            setScrolled(true)
            getNameHouse(activeConv.house_id).then((response) => {
                setHouseName(response)
            })
        }
    }, [scrolled, activeConv])

    useEffect(() => {
        if (chater.length) {
            const queryStr = Array.from(
                new Set(chater.map((item) => item[typeUserID]))
            )
                .map((item) => `user_ids=${item}`)
                .join("&");
            UserAPI.get(API_USERS + "short-description/?" + queryStr)
                .then((response) => {
                    if (
                        !response.hasOwnProperty("users_short_description") ||
                        !!!response.users_short_description.length
                    ) {
                        setLoader(false)
                        setConversations([]);
                    } else {
                        const users = Object.fromEntries(
                            response.users_short_description.map((item: TUserShot) => [
                                item.user_id,
                                {
                                    first_name: item.first_name!,
                                    last_name: item.last_name!,
                                    thumbnail_url: item.thumbnail_url!,
                                },
                            ])
                        );
                        let newConversation: TConversation[] = chater
                            .filter((item) => Object.keys(users).includes(item[typeUserID]))
                            .map((item) => ({
                                lastMessageTime: item.chatted ? Math.round((new Date().getTime() - new Date(new Date(item.updated_at).toISOString()).getTime()) / DAY_UNIT_IN_MILLISECONDS) >= 1 ? convertTime(item.updated_at).slice(0, 9) : convertTime(item.updated_at).slice(-5) : '',
                                chat_id: item.chat_id,
                                user_id: item[typeUserID],
                                house_id: item.house_id,
                                ...users[item[typeUserID]],
                                unread: userProfile?.user_type === 'host' ? ((item.host_most_recent_read_at !== item.host_most_recent_unread_at) && (item.host_most_recent_unread_at)) : ((item.student_most_recent_read_at !== item.student_most_recent_unread_at) && (item.student_most_recent_unread_at))
                            }));
                        setConversations(prev => [...prev, ...newConversation]);
                    }
                    isEmpty(!conversations.length);
                    setLoader(false)
                })
                .catch(() => {
                    setLoader(false)
                    setConversations([]);
                });
        }
        // eslint-disable-next-line
    }, [chater]);

    useEffect(() => {
        if (searchedChatter.length) {
            const queryStr = Array.from(
                new Set(searchedChatter.map((item) => item[typeUserID]))
            )
                .map((item) => `user_ids=${item}`)
                .join("&");
            UserAPI.get(API_USERS + "short-description/?" + queryStr)
                .then((response) => {
                    if (
                        !response.hasOwnProperty("users_short_description") ||
                        !!!response.users_short_description.length
                    ) {
                        setLoader(false)
                        // setConversations([]);
                    } else {
                        const users = Object.fromEntries(
                            response.users_short_description.map((item: TUserShot) => [
                                item.user_id,
                                {
                                    first_name: item.first_name!,
                                    last_name: item.last_name!,
                                    thumbnail_url: item.thumbnail_url!,
                                },
                            ])
                        );
                        let newConversation: TConversation[] = searchedChatter
                            .filter((item) => Object.keys(users).includes(item[typeUserID]))
                            .map((item) => ({
                                lastMessageTime: item.chatted ? Math.round((new Date().getTime() - new Date(new Date(item.updated_at).toISOString()).getTime()) / DAY_UNIT_IN_MILLISECONDS) >= 1 ? convertTime(item.updated_at).slice(0, 9) : convertTime(item.updated_at).slice(-5) : '',
                                chat_id: item.chat_id,
                                user_id: item[typeUserID],
                                house_id: item.house_id,
                                ...users[item[typeUserID]],
                                unread: userProfile?.user_type === 'host' ? ((item.host_most_recent_read_at !== item.host_most_recent_unread_at) && (item.host_most_recent_unread_at)) : ((item.student_most_recent_read_at !== item.student_most_recent_unread_at) && (item.student_most_recent_unread_at))
                            }));
                        setSearchedConversations(newConversation)
                    }
                    // isEmpty(!conversations.length);
                    setLoader(false)
                })
                .catch(() => {
                    setLoader(false)
                    // setConversations([]);
                });
        } else {
            setSearchedConversations([])
        }
        // eslint-disable-next-line
    }, [searchedChatter]);

    const getNameHouse = async (house_id: string) => {
        if (userProfile?.user_type === "host") {
            return await new Promise((resolve, reject) => {
                const itemHouse: IHousesShotInfo[] = housesShotData.filter(
                    (item) => item.house_id === house_id
                );
                resolve(!!itemHouse.length ? itemHouse[0].title : "");
            });
        } else {
            const response = await StudentAPI.get(API_STUDENT + house_id);
            return Object.keys(response).includes("title") ? response.title : "";
        }
    };

    const getNameActiveConv = (chatID: string) => {
            const currentConv = showSearched ? searchedConversations : conversations
            if (!!chatID.length) {
                const activeUser = currentConv.filter((item) => item).find(
                    ({chat_id}) => chat_id === chatID
                );
                return activeUser
                    ? `${activeUser.first_name}`
                    : "";
            }
        return "";
    };

    const loadConv = async (query?: string) => {
        setLoading(true)
        ChatsAPI.get(API_CHATS + "user-chats/" + userProfile?.user_id! + `${query ? '?next_batch_token=' + query : ''}`).then(
            (res) => {
                setChater(res.chats);
                setBatchToken(res.next_batch_token ?? '')
                setLoading(false)
                !res.chats.length && setLoader(false)
            }
        );
    };

    useEffect(() => {
        (async () => {
            if (location.search) {
                let [_, q_chat, q_house] = location.search.split('=')
                const currentConv = showSearched ? searchedConversations : conversations

                    const qHouseName = q_house ? await getNameHouse(q_house.includes('&s') ? q_house.slice(0, -2) : q_house).then((response) => response) : undefined
                    q_chat = q_house ? q_chat.slice(0, q_chat.length - 2) : q_chat

                    const conv = currentConv.filter((item) => item).find(({chat_id}) => chat_id === q_chat)
                    conv && setActiveConv({
                        chat_id: conv.chat_id,
                        house_id: conv.house_id,
                        name: conv.first_name as string,
                        name_house: qHouseName || houseName,
                        user_id: conv.user_id,
                    })
            } else {
                setActiveConv({
                    chat_id: '',
                    house_id: '',
                    name: '',
                    name_house: '',
                    user_id: '',
                })
            }
        })()

    }, [conversations, houseName, location, searchedConversations, showSearched])

    const handleConversation: THandleSingleConv = (
        chat_id_selected,
        varMode,
        user_id_selected,
        house_id_selected
    ) => {
        switch (varMode) {
            case "view":
                showProfile(user_id_selected);
                break;
            case "set":
                setLoading(true);
                setUnreadConversations(unreadConversations.filter((conv) => conv !== chat_id_selected))
                setLatestRead([])
                const path = `/${i18n.language}/chats?c=${chat_id_selected}${house_id ? `&h=${house_id}` : ''}${user_id ? `&s=${user_id}` : ''}`
                location.search.length ? navigate(path, { replace: true }) : (
                    house_id ? navigate(path, { replace: true }) : navigate(path)
                )
                getNameHouse(house_id || house_id_selected)
                    .then((response) => {
                        setHouseName(response)
                    })
                    .finally(() => setLoading(false));
                break;
            case "report":
                setCurrentChat(`${chat_id_selected},${user_id_selected}`);
                setShowModal(1);
                break;
            case "delete":
                setCurrentChat(`${chat_id_selected},${user_id_selected}`);
                setDeleteChatPopover(chat_id_selected)
                break;
            default:
                console.log("Remove chat");
                break;
        }
    };
    const openInviteDlg = () => {
        UserAPI.get(API_USERS + activeConv.user_id + "/user-preview").then(
            (userInfo: Partial<IPerson>) => {
                if (
                    userInfo.preferred_move_in === undefined ||
                    userInfo.preferred_move_out === undefined
                )
                    dateRange.onChange(getPeriod().value, getPeriod().endInterval);
                else
                    dateRange.onChange(
                        new Date(userInfo.preferred_move_in as string),
                        new Date(userInfo.preferred_move_out as string)
                    );
                setShowModal(2);
            }
        );
    };
    const sendInvite = () => {
        const payload: IMatchesEnum = {
            host_id: userProfile?.user_id!,
            house_id: activeConv.house_id,
            student_id: activeConv.user_id,
            student_check_in_date: isoDate(dateRange.value),
            student_check_out_date: isoDate(dateRange.endInterval),
        };
        handleMatch("acceptInvite", payload).then((response) => {
            if (response === 200) console.log("Success");
            else console.log("Error");
        });
        setDisableInviteButton([...disableInviteButton, activeConv.house_id])
        setShowModal(0);
    };

    useEffect(() => {
        if (!dateRange.value || !dateRange.endInterval) {
            setInviteError(t_match("host.startAndEnd"))
        } else if (dateRange.value?.toISOString().slice(0,-7) === dateRange.endInterval?.toISOString().slice(0,-7)) {
            setInviteError(t_match("host.sameDate"))
        } else {
            setInviteError('')
        }
    }, [dateRange.endInterval, dateRange.value]);

    useEffect(() => {
        isEmpty(!!!conversations.length);
        if (modePage === "preselected" && !!conversations.length) {
            const foundConv = conversations.find(
                (item) => item.house_id === house_id && item.user_id === user_id
            );
            if (foundConv && activeConv.chat_id !== foundConv.chat_id) {
                handleConversation(foundConv.chat_id, "set", user_id, house_id);
                setConversations((prev) => [
                    foundConv,
                    ...conversations.filter((item) => item.chat_id !== foundConv.chat_id),
                ]);
            }
        }
        // eslint-disable-next-line
    }, [conversations]);
    const [cur_chat, cur_user] = curerntChat.split(",");

    const sendReport = () => {
        if (!!curerntChat.length) {
            setLoading(true);
            ChatsAPI.post(API_CHATS + "report_chat", {
                chat_id: cur_chat,
                reporter_id: userProfile?.user_id!,
                reportee_id: cur_user,
            })
                .then((res) => {
                    setShowModal(3);
                })
                .finally(() => setLoading(false));
            setShowModal(0);
        }
    };

    const correctStyleDlg: React.CSSProperties = {
        maxWidth: "400px",
        width: "80vw",
    };

    const scrollHandler = (e: any, scrollTop?: boolean) => {
        const element = e.currentTarget;
        scrollTop && element.scrollTo(0,0)
        if(element.scrollHeight - element.scrollTop === element.clientHeight && showSearched && searchPage < Math.ceil(searchedChatter.length / 7)) {
            setSearchPage(prev => prev + 1)
        }
        if (element.scrollHeight - element.scrollTop === element.clientHeight && batchToken && !showSearched) {
            setLoadAdditionalConv(prev => prev + 1)
        }
    };

    useEffect(() => {
        !!loadAdditionalConv && loadConv(batchToken)
    }, [loadAdditionalConv])

    if (!!!chater.length) return <div/>;

    return (
        <>
            {
                deleteChatPopover && <DeleteChatPopover>
                    <div>
                        <TitleH2 className="titleDel">{t('deleteChat')}</TitleH2>
                        <div>
                            <Button
                                onClick={() => setDeleteChatPopover('')}
                                normal
                                className="butCancel"
                            >
                                Cancel
                            </Button>
                            <Button danger className="butDel"
                                    onClick={() => ChatsAPI.post(`${API_CHATS}remove-chat/${deleteChatPopover}`, {
                                        chat_id: cur_chat,
                                    }).then(() => {
                                        closeChat(true)
                                        setConversations((prev) => [...prev.filter(({chat_id}) => chat_id !== deleteChatPopover)])
                                        setDeleteChatPopover('')
                                    })}>
                                Delete
                            </Button>
                        </div>
                    </div>
                </DeleteChatPopover>
            }
            <Conversations {...props}>
                {(!isPhone || (isPhone && !!!activeConv.chat_id)) && (
                    <>
                        <ConversationsTitle>
                            {t("chater")}
                            {loading && <LoaderCustom isModal={false}/>}
                        </ConversationsTitle>
                        <ConversationSearchSection>
                            <input value={searchPattern}
                                   onChange={(e) => handleSearchInputChange(e.currentTarget.value)}
                                   onKeyPress={handleEnter}
                            />
                            {
                                (showSearched && !typedAfterSearch) ? (
                                    <button onClick={() => {
                                        convRef.current && convRef.current.scrollTo(0,0)
                                        setSearchPage(1)
                                        setShowSearched(false)
                                        setSearchedConversations([])
                                        setSearchedChatter([])
                                        setSearchPattern('')
                                        setTypedAfterSearch(false)
                                        activeConv.chat_id && setScrollAfterClear(true)
                                    }}>
                                        {t('butClear')}
                                    </button>
                                ) : (
                                    <button onClick={handleSearchChats} disabled={!searchPattern.trim() || searchProgress}>
                                        {t('butSearch')}
                                    </button>
                                )
                            }
                        </ConversationSearchSection>
                        <ConversationList id="convSection" ref={convRef} onScroll={scrollHandler}>
                            <div>
                                {(showSearched ? unique([...searchedConversations].slice(0, 7 * searchPage)) : unique(conversations))?.map((item, index) => (
                                    <div
                                        ref={(showSearched || searchProgress) ? undefined : refs[item.chat_id]}
                                    >
                                        <SingleConversation
                                            lastMessageDate={lastMessageDate[item.chat_id]}
                                            lastMessageTime={item.lastMessageTime}
                                            unreadConversations={unreadConversations}
                                            setUnreadConversations={setUnreadConversations}
                                            closeChat={closeChat}
                                            key={index}
                                            unread={unreadConversations.includes(item.chat_id)}
                                            handleActiveConversation={handleConversation}
                                            person={item}
                                            isActive={activeConv.chat_id === item.chat_id}
                                        />
                                    </div>
                                ))}
                            </div>
                            {showSearched && !searchProgress && !searchedChatter.length ? <div style={{width: '100%', justifyContent: 'center', display: 'flex'}}>
                                {t('noChatsFound')}
                            </div> : ''}
                        </ConversationList>
                    </>
                )}
                <InviteWrapper>
                    {!disableInviteButton.includes(activeConv.house_id) && userProfile?.user_type! === "host" && !!activeConv.chat_id.length && (
                        <Button style={{fontSize: "12px"}} outline onClick={openInviteDlg}>{`${t(
                            "host.buttons.chooseStudent"
                        )} ${getNameActiveConv(activeConv.chat_id)} ${t(
                            "host.buttons.chooseStudentTwo"
                        )}`}</Button>
                    )}
                </InviteWrapper>
            </Conversations>
            {!!showModal && (
                <Modal isShow={!!showModal} handleClose={() => setShowModal(0)}>
                    {showModal === 1 && (
                        <DialogWrapper style={correctStyleDlg}>
                            <TitleH2>{`${t("report.title")} ${getNameActiveConv(
                                activeConv.chat_id
                            )}`}</TitleH2>
                            <TextPlain style={{wordBreak: "break-word"}}>
                                {t("report.text")}
                            </TextPlain>

                            <div className="buttons">
                                <Button
                                    normal
                                    onClick={() => setShowModal(0)}
                                    className="butCancel"
                                >
                                    {t("butCancel")}
                                </Button>
                                <Button danger onClick={sendReport} className="butDel">
                                    {t("report.butSendreport")}
                                </Button>
                            </div>
                        </DialogWrapper>
                    )}
                    {showModal === 2 && (
                        <DialogWrapper>
                            <TitleH2>{t_match("host.dateRange.title")}</TitleH2>
                            <div>
                                <TextPlain style={{wordBreak: "break-word"}}>{`${t_match(
                                    "host.dateRange.text"
                                )} ${activeConv.name}?`}</TextPlain>
                                <div className="cal">
                                    <Calendar {...dateRange} error={inviteError}/>
                                    <div style={{ textAlign: 'center', marginTop: '2px', color: 'red', wordBreak: 'break-word' }}>
                                        {inviteError}
                                    </div>
                                </div>
                            </div>
                            <div className="buttons">
                                <Button normal onClick={() => setShowModal(0)}>
                                    {t_match("host.dateRange.cancel")}
                                </Button>
                                <Button main disabled={!!inviteError} onClick={() => sendInvite()}>
                                    {t_match("host.dateRange.buttonPropose")}
                                </Button>
                            </div>
                        </DialogWrapper>
                    )}
                    {showModal === 3 && (
                        <DialogWrapper>
                            <TitleH2>{t("info")}</TitleH2>
                            <TextPlain style={{wordBreak: "break-word"}}>
                                {t("report.sendReport")}
                            </TextPlain>
                            <div className="buttons">
                                <span/>
                                <Button
                                    normal
                                    onClick={() => setShowModal(0)}
                                    style={{width: "6em"}}
                                >
                                    OK
                                </Button>
                            </div>
                        </DialogWrapper>
                    )}
                </Modal>
            )}
        </>
    );
};

const SingleConversation: React.FC<ISingleConversation> = ({
    lastMessageDate,
    lastMessageTime,
    person,
    isActive,
    handleActiveConversation,
    setUnreadConversations,
    unreadConversations,
    unread: conv_unread,
    closeChat,
}) => {
    const { first_name, last_name, thumbnail_url, chat_id, user_id, house_id, unread } =
        person;

    const [isVisible, setIsVisible] = useState(false);
    const outsideTracker = useRef(null);
    useOutsideAlerter(outsideTracker, setIsVisible);

    const [isRead, setIsRead] = useState<boolean>(false)

    useEffect(() => {
        isRead && setIsRead(false)
    }, [unreadConversations, isRead])

    const handleConv = (varMode: TModeConv) => {
        handleActiveConversation(chat_id, varMode, user_id, house_id);
        setIsVisible(false);
    };

    return (
        <ItemLi
            ref={outsideTracker}
            isActive={isActive}
            onClick={() => {
                setUnreadConversations(prev => prev.filter((conv: string) => conv !== chat_id))
                closeChat(false)
                handleConv("set");
            }}
        >
            <img src={thumbnail_url + `?${new Date().toString()}`} alt="avatar" />
            <p style={{
                fontSize: "12px",
                display: 'flex',
                alignItems: 'center'
            }}>
                <div>
                    <span>{`${first_name}`}</span>
                    <span>{`${lastMessageDate ? convertTime(lastMessageDate).slice(-5) : lastMessageTime.includes('-') ? `${lastMessageTime.split('-')[1]}-${lastMessageTime.split('-')[0]}-${lastMessageTime.split('-')[2]}` : lastMessageTime}`}</span>
                </div>
                {((!conv_unread ? false : conv_unread || unread) && !isRead) &&
                    <div style={{ flex: 1, alignItems: 'center' }}>
                        <div style={{ width: 5, height: 5, borderRadius: '50%', marginLeft: 8, background: '#032565' }} />
                    </div>}</p>
            <div
                onClick={(e) => {
                    setIsVisible(!isVisible);
                    e.stopPropagation();
                }}
                id="dots"
            >
                <IcoThreeDots
                    size="1.5em"
                    color={isActive ? theme.colors.bgBody : theme.colors.textDimmed}
                />
            </div>
            <ConversationDropDownMenu handleConv={handleConv} isVisible={isVisible} />
        </ItemLi>
    );
}
export const ConversationDropDownMenu: React.FC<{
    isVisible: boolean;
    handleConv: (mode: TModeConv) => void;
}> = ({ isVisible, handleConv }) => {
    const { t } = useTranslation("chats");

    return (
        <DropDownMenu isVisible={isVisible}>
            <ul>
                {(["view", "report", "delete"] as TModeConv[]).map((item) => (
                    <li
                        onClick={(e) => {
                            handleConv(item);
                            e.stopPropagation();
                        }}
                        key={item}
                    >
                        {t("popupChater." + item)}
                    </li>
                ))}
            </ul>
        </DropDownMenu>
    );
};
